<template>
  <div class="curana-assessment bg-white border border-gray-200 rounded-lg">
    <div class="flex items-center justify-between px-8 py-4 border-b border-gray-200">
      <div class="flex items-center gap-2 text-xs text-gray-500">
        <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" class="text-blue-500" />
        AI-generated forensic assessment · A5069 · {{ today }}
      </div>
      <ExportMenu :exports="[{ type: 'pdf', action: downloadPdf }]" />
    </div>

    <!-- Memo body -->
    <article class="memo max-w-none px-8 py-6 text-gray-900">
      <header class="memo-header mb-4 pb-2 border-b border-gray-900">
        <div class="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">AI Contract Assessment · Memo</div>
        <h1 class="text-xl font-semibold mt-1 mb-0.5">A5069 — Cost Decomposition, 2024 Settlement → 2025 Q4</h1>
        <div class="text-[11px] text-gray-600">Elite Patient Care, LLC (Curana Health) · {{ today }}</div>
      </header>

      <section class="mb-3">
        <h2 class="memo-h2">The headline</h2>
        <p>
          Contract cost per person-year fell from <strong>$37,028 to $36,271</strong> between the 2024 settlement and the 2025 Q4 rolling report — a 2.0% improvement. When the panel is split into the beneficiaries who left, the beneficiaries who stayed, and the beneficiaries who joined, the 2% moves almost entirely through the turnover cohorts. The stayer cohort's trajectory does not explain it.
        </p>
      </section>

      <section class="mb-3">
        <h2 class="memo-h2">How the panel turned over</h2>
        <p>
          The attributed population grew from <strong>16,361 to 24,238</strong>, a net addition of 7,877. That net number masks substantial churn underneath: 7,761 of the original 16,361 beneficiaries were no longer attributed a year later (the <strong>leavers</strong>), and 15,638 beneficiaries appeared for the first time (the <strong>joiners</strong>). Only <strong>8,600 beneficiaries — 53% of the 2024 panel — were attributed in both periods</strong>.
        </p>
        <p>
          The three cohorts cost very different amounts. Leavers ran <strong>$45,717 per PY</strong> — well above the ACO average — and 56% of them are flagged as deceased in the data. The ACO's 2024 panel was dominated by very sick, end-of-life beneficiaries, and the turnover shed that cost base. Stayers moved from <strong>$28,499 to $30,052 per PY</strong> (+5.4%). Joiners came in at <strong>$38,094 per PY</strong> — $7,623 cheaper than the leavers they replaced, $8,042 higher than the 2024 stayer baseline. Replacing $45K leavers with $38K joiners, while the stayer block drifted up 5%, is arithmetically what produced the overall 2% decline.
        </p>
        <p>
          Inside the stayer cohort, the category pattern is consistent with an aging nursing-home population: SNF spending fell $1,639 per PY (−27%) while hospice rose $2,683 per PY (roughly doubling). IP, outpatient, and Part B were approximately flat. The net +5.4% is the sum of that substitution plus smaller moves in the other categories.
        </p>
      </section>

      <section class="mb-3">
        <h2 class="memo-h2">Which practices drove the turnover math</h2>
        <p>
          Benchmark impact per TIN — the dollar effect of each practice's specific leaver/joiner cost gap — shows most of the ACO's growth TINs running deflationary. Curana's <strong>North Carolina practice contributed −$11.1M</strong> on its own: 763 leavers at $40K/PY were replaced by 1,114 joiners at $25K/PY. South Carolina contributed −$3.2M, Pennsylvania −$2.4M, Arizona −$2.2M, Massachusetts −$1.6M. In each, joiner cost per PY ran meaningfully below leaver cost per PY.
        </p>
        <p>
          Barnes Health (TIN 923931499) appears in the raw data as a practice that lost 939 beneficiaries and gained 20 — a collapse. At the NPI level that isn't what happened: <strong>35 of Barnes's 49 providers now bill under Curana Missouri-Kansas (TIN 770628171)</strong>, and 529 of their patients followed them. The movement reflects an internal reassignment (Curana acquired Barnes and re-parented its providers), not attrition. The other 979 Barnes patients left the ACO entirely. On the reassignment‑destination side, Missouri-Kansas shows 1,463 joiners at $35,552/PY against 586 leavers at $47,022/PY — contributing −$0.9M.
        </p>
        <p>
          One TIN moves the mean in the opposite direction. <strong>Trumen Physicians (TIN 272022354), the Houston flagship, contributed +$19.8M</strong>. Its joiner cohort came in at $63,021/PY against a leaver cohort at $60,183/PY — the joiners are more expensive than the patients they're replacing. This is the only material TIN in A5069 where the replacement arithmetic is net inflationary, and it's large enough in absolute dollars to offset Curana North Carolina nearly 2×.
        </p>
      </section>

      <section class="mb-3">
        <h2 class="memo-h2">What's distinctive about the Trumen cohort</h2>
        <p>
          Trumen's <strong>1,338 stayers moved from $45,847 to $46,981 per PY (+2.5%)</strong> — below the panel-wide stayer drift, with the same SNF-to-hospice substitution seen elsewhere (SNF −$3,232, hospice +$2,996, other categories approximately flat). The stayer cohort tracks the rest of the ACO.
        </p>
        <p>
          The joiners do not. Comparing Trumen's 2,578 joiners to its 735 leavers: average age is slightly younger (81.0 vs 81.7), but <strong>the full dual-eligible share is 33.0% in joiners vs 24.5% in leavers</strong>, a 9-point shift. ESRD share nearly doubles (1.9% vs 1.0%). Facility mix also shifts: <strong>67% of joiner spend flows through facilities averaging more than $60K per PY</strong>, against 38% of stayer spend. Within those facilities, joiner cost per PY does not track CMS star rating — it runs $59,664 at 5‑star sites, $68,167 at 4‑star, $66,050 at 3‑star, $66,333 at 2‑star, and $61,573 at 1‑star. At every quality tier, Trumen joiners cost roughly $20K per PY more than matched stayers.
        </p>
        <p>
          The utilization fingerprint at Trumen differs from the rest of A5069. On 2025 Q4 rolling data, Trumen patients receive <strong>12.7 PCP visits per beneficiary per year vs 6.5 in the rest of the ACO</strong> — nearly twice as many. They also generate <strong>978 IP discharges per 1,000 vs 606</strong>, <strong>776 ED-to-IP conversions per 1,000 vs 515</strong>, and 1,715 ED visits per 1,000 vs 1,247. The two patterns — higher primary care contact and higher acute utilization — coexist in the same population.
        </p>
        <p>
          Within acute utilization, one sub-category stands out. Ambulatory-care-sensitive conditions — admissions coded as COPD exacerbations, CHF, UTIs, bacterial pneumonia, and diabetes complications — run at <strong>199 IP admits per 1,000 attributed patients at Trumen vs 136 per 1,000 in the rest of the ACO</strong>, a 46% rate differential. Average cost per ACSC episode is $12,554 vs $9,003. These are the admissions the AHRQ PQI framework classifies as potentially preventable by ambulatory management; the rate gap is consistent across the entire Trumen attributed panel, not concentrated in joiners specifically.
        </p>
        <p>
          The stayer-level cost variance at Trumen is also concentrated, not distributed. Of 1,336 stayers, <strong>184 (14%) had cost increases exceeding $50K</strong>, moving from an average of $47,875 to $159,174 (net +$20.5M). In the opposite direction, 112 stayers decreased by more than $50K, from $137,383 to $44,443 (net −$10.4M). The majority of the cohort (roughly 700 beneficiaries) moved ±$5K. The stayer +2.5% is the net of a modestly rising middle and two sharp tails.
        </p>
        <p>
          Under the hood, Trumen's growth was absorbed by its existing provider base. <strong>106 NPIs billed under Trumen in both 2024 and 2025 and now carry 1,775 of Trumen's 2025 beneficiaries</strong> at $58,089 per PY (an average of 17 attributed benes per provider, roughly triple the 2024 stayer-ratio of 6 per provider). 25 new NPIs joined in 2025 and carry only 187 beneficiaries at $52,197 per PY — the new hires are billing fewer and cheaper patients than the retained cohort. Growth was not a hiring story.
        </p>
      </section>

      <section class="mb-3">
        <h2 class="memo-h2">Risk-adjusted comparisons are not available for this pair</h2>
        <p>
          CMS used HCC model V24 on the 2024 settlement AALR (scores based on 2023 diagnoses) and HCC model V28 on the 2025 Q4 QALR (scores based on 2024 diagnoses). Raw V24 panel average on the 2024 AALR: 1.526. Raw V28 panel average on the 2025 Q4 QALR: 1.477. These values are not on a common scale, and any risk-adjusted cost trend computed across the pair reflects the model change alongside any change in measured risk. Raw per-PY cost and cohort decomposition are the readable signals for this period.
        </p>
      </section>

      <section class="mb-3">
        <h2 class="memo-h2">Shared savings context</h2>
        <p>
          The 2024 settlement produced <strong>$81.1M in earned shared savings</strong>: total benchmark of $661.4M against total expenditure of $551.0M on 14,881 person-years, a per-PY benchmark of $44,442 against actual of $37,028. Benchmark years were BY1=2020, BY2=2021, BY3=2023. Per STLMT Table A1, the CMS risk ratio was capped at 1.03 for all four enrollment types; the uncapped ratios were 0.896–0.970, meaning the cap constrained a larger downward risk adjustment that would otherwise have pulled the benchmark closer to the measured panel health. The 2025 trajectory inherits that benchmark structure.
        </p>
      </section>

      <footer class="mt-10 pt-4 border-t border-gray-200 text-[10px] text-gray-500 leading-relaxed">
        <div>Methodology: person-year-weighted per CMS QEXPU. Cohorts derived from ATTRIB_ALL_REPORTS membership at 2024-12-31 vs 2025-12-01; cost from BEUR (TOT_CAP_ANN_EXP × BENE_PSNYRS); TIN attribution via ASSIGN_CURRENT. Panel cost/PY calculation validates within ~2% of CMS Table 1 (residual gap = BEUR data-sharing opt-outs). Risk-adjusted cost deliberately excluded due to V24→V28 model change between the two reports.</div>
      </footer>
    </article>
  </div>
</template>

<script>
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import jsPDF from 'jspdf';

const SECTIONS = [
  {
    title: 'The headline',
    body: [
      "Contract cost per person-year fell from $37,028 to $36,271 between the 2024 settlement and the 2025 Q4 rolling report — a 2.0% improvement. When the panel is split into the beneficiaries who left, the beneficiaries who stayed, and the beneficiaries who joined, the 2% moves almost entirely through the turnover cohorts. The stayer cohort's trajectory does not explain it.",
    ],
  },
  {
    title: 'How the panel turned over',
    body: [
      "The attributed population grew from 16,361 to 24,238, a net addition of 7,877. That net number masks substantial churn underneath: 7,761 of the original 16,361 beneficiaries were no longer attributed a year later (the leavers), and 15,638 beneficiaries appeared for the first time (the joiners). Only 8,600 beneficiaries — 53% of the 2024 panel — were attributed in both periods.",
      "The three cohorts cost very different amounts. Leavers ran $45,717 per PY — well above the ACO average — and 56% of them are flagged as deceased in the data. The ACO's 2024 panel was dominated by very sick, end-of-life beneficiaries, and the turnover shed that cost base. Stayers moved from $28,499 to $30,052 per PY (+5.4%). Joiners came in at $38,094 per PY — $7,623 cheaper than the leavers they replaced, $8,042 higher than the 2024 stayer baseline. Replacing $45K leavers with $38K joiners, while the stayer block drifted up 5%, is arithmetically what produced the overall 2% decline.",
      "Inside the stayer cohort, the category pattern is consistent with an aging nursing-home population: SNF spending fell $1,639 per PY (-27%) while hospice rose $2,683 per PY (roughly doubling). IP, outpatient, and Part B were approximately flat. The net +5.4% is the sum of that substitution plus smaller moves in the other categories.",
    ],
  },
  {
    title: 'Which practices drove the turnover math',
    body: [
      "Benchmark impact per TIN — the dollar effect of each practice's specific leaver/joiner cost gap — shows most of the ACO's growth TINs running deflationary. Curana's North Carolina practice contributed -$11.1M on its own: 763 leavers at $40K/PY were replaced by 1,114 joiners at $25K/PY. South Carolina contributed -$3.2M, Pennsylvania -$2.4M, Arizona -$2.2M, Massachusetts -$1.6M. In each, joiner cost per PY ran meaningfully below leaver cost per PY.",
      "Barnes Health (TIN 923931499) appears in the raw data as a practice that lost 939 beneficiaries and gained 20 — a collapse. At the NPI level that isn't what happened: 35 of Barnes's 49 providers now bill under Curana Missouri-Kansas (TIN 770628171), and 529 of their patients followed them. The movement reflects an internal reassignment (Curana acquired Barnes and re-parented its providers), not attrition. The other 979 Barnes patients left the ACO entirely. On the reassignment-destination side, Missouri-Kansas shows 1,463 joiners at $35,552/PY against 586 leavers at $47,022/PY — contributing -$0.9M.",
      "One TIN moves the mean in the opposite direction. Trumen Physicians (TIN 272022354), the Houston flagship, contributed +$19.8M. Its joiner cohort came in at $63,021/PY against a leaver cohort at $60,183/PY — the joiners are more expensive than the patients they're replacing. This is the only material TIN in A5069 where the replacement arithmetic is net inflationary, and it's large enough in absolute dollars to offset Curana North Carolina nearly 2x.",
    ],
  },
  {
    title: "What's distinctive about the Trumen cohort",
    body: [
      "Trumen's 1,338 stayers moved from $45,847 to $46,981 per PY (+2.5%) — below the panel-wide stayer drift, with the same SNF-to-hospice substitution seen elsewhere (SNF -$3,232, hospice +$2,996, other categories approximately flat). The stayer cohort tracks the rest of the ACO.",
      "The joiners do not. Comparing Trumen's 2,578 joiners to its 735 leavers: average age is slightly younger (81.0 vs 81.7), but the full dual-eligible share is 33.0% in joiners vs 24.5% in leavers, a 9-point shift. ESRD share nearly doubles (1.9% vs 1.0%). Facility mix also shifts: 67% of joiner spend flows through facilities averaging more than $60K per PY, against 38% of stayer spend. Within those facilities, joiner cost per PY does not track CMS star rating — it runs $59,664 at 5-star sites, $68,167 at 4-star, $66,050 at 3-star, $66,333 at 2-star, and $61,573 at 1-star. At every quality tier, Trumen joiners cost roughly $20K per PY more than matched stayers.",
      "The utilization fingerprint at Trumen differs from the rest of A5069. On 2025 Q4 rolling data, Trumen patients receive 12.7 PCP visits per beneficiary per year vs 6.5 in the rest of the ACO — nearly twice as many. They also generate 978 IP discharges per 1,000 vs 606, 776 ED-to-IP conversions per 1,000 vs 515, and 1,715 ED visits per 1,000 vs 1,247. The two patterns — higher primary care contact and higher acute utilization — coexist in the same population.",
      "Within acute utilization, one sub-category stands out. Ambulatory-care-sensitive conditions — admissions coded as COPD exacerbations, CHF, UTIs, bacterial pneumonia, and diabetes complications — run at 199 ACSC IP admits per 1,000 attributed patients at Trumen vs 136 per 1,000 in the rest of the ACO, a 46% rate differential. Average cost per ACSC episode is $12,554 vs $9,003. These are the admissions the AHRQ PQI framework classifies as potentially preventable by ambulatory management; the rate gap is consistent across the entire Trumen attributed panel, not concentrated in joiners specifically.",
      "The stayer-level cost variance at Trumen is also concentrated, not distributed. Of 1,336 stayers, 184 (14%) had cost increases exceeding $50K, moving from an average of $47,875 to $159,174 (net +$20.5M). In the opposite direction, 112 stayers decreased by more than $50K, from $137,383 to $44,443 (net -$10.4M). The majority of the cohort (roughly 700 beneficiaries) moved +/-$5K. The stayer +2.5% is the net of a modestly rising middle and two sharp tails.",
      "Under the hood, Trumen's growth was absorbed by its existing provider base. 106 NPIs billed under Trumen in both 2024 and 2025 and now carry 1,775 of Trumen's 2025 beneficiaries at $58,089 per PY (an average of 17 attributed benes per provider, roughly triple the 2024 stayer-ratio of 6 per provider). 25 new NPIs joined in 2025 and carry only 187 beneficiaries at $52,197 per PY — the new hires are billing fewer and cheaper patients than the retained cohort. Growth was not a hiring story.",
    ],
  },
  {
    title: 'Risk-adjusted comparisons are not available for this pair',
    body: [
      "CMS used HCC model V24 on the 2024 settlement AALR (scores based on 2023 diagnoses) and HCC model V28 on the 2025 Q4 QALR (scores based on 2024 diagnoses). Raw V24 panel average on the 2024 AALR: 1.526. Raw V28 panel average on the 2025 Q4 QALR: 1.477. These values are not on a common scale, and any risk-adjusted cost trend computed across the pair reflects the model change alongside any change in measured risk. Raw per-PY cost and cohort decomposition are the readable signals for this period.",
    ],
  },
  {
    title: 'Shared savings context',
    body: [
      "The 2024 settlement produced $81.1M in earned shared savings: total benchmark of $661.4M against total expenditure of $551.0M on 14,881 person-years, a per-PY benchmark of $44,442 against actual of $37,028. Benchmark years were BY1=2020, BY2=2021, BY3=2023. Per STLMT Table A1, the CMS risk ratio was capped at 1.03 for all four enrollment types; the uncapped ratios were 0.896-0.970, meaning the cap constrained a larger downward risk adjustment that would otherwise have pulled the benchmark closer to the measured panel health. The 2025 trajectory inherits that benchmark structure.",
    ],
  },
];

const FOOTNOTE = "Methodology: person-year-weighted per CMS QEXPU. Cohorts derived from ATTRIB_ALL_REPORTS membership at 2024-12-31 vs 2025-12-01; cost from BEUR (TOT_CAP_ANN_EXP x BENE_PSNYRS); TIN attribution via ASSIGN_CURRENT. Panel cost/PY calculation validates within ~2% of CMS Table 1 (residual gap = BEUR data-sharing opt-outs). Risk-adjusted cost deliberately excluded due to V24->V28 model change between the two reports.";

export default {
  name: 'CuranaContractAssessment',
  components: { ExportMenu },
  data() {
    return {
      today: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    };
  },
  methods: {
    cleanTextForPdf(text) {
      if (!text) return '';
      return text
        .normalize('NFKD')
        .replace(/[^\x20-\x7E\n\r\t]/g, (char) => {
          const map = {
            '\u2192': '->', '\u2190': '<-', '\u2194': '<->',
            '\u2018': "'", '\u2019': "'", '\u201c': '"', '\u201d': '"',
            '\u2013': '-', '\u2014': '--', '\u2026': '...',
            '\u2011': '-', '\u00A0': ' ',
          };
          return map[char] || '';
        });
    },

    downloadPdf() {
      const doc = new jsPDF('p', 'pt');
      const pageWidth = 595;
      const pageHeight = 842;
      const margin = 50;
      const contentWidth = pageWidth - margin * 2;
      let y = margin;

      const checkPageBreak = (needed = 60) => {
        if (y > pageHeight - margin - needed) {
          doc.addPage();
          y = margin;
        }
      };

      const addText = (text, fontSize = 10, font = 'normal', lineHeight = null) => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', font);
        const lh = lineHeight || fontSize + 3;
        const lines = doc.splitTextToSize(this.cleanTextForPdf(text || ''), contentWidth);
        for (const line of lines) {
          checkPageBreak(lh);
          doc.text(line, margin, y);
          y += lh;
        }
      };

      // ===== TITLE BLOCK =====
      const ct = (t) => this.cleanTextForPdf(t);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(107, 114, 128);
      doc.text(ct('AI CONTRACT ASSESSMENT  |  MEMO'), margin, y);
      y += 18;

      doc.setFontSize(16);
      doc.setTextColor(17, 24, 39);
      doc.text(ct('A5069 - Cost Decomposition, 2024 Settlement to 2025 Q4'), margin, y);
      y += 16;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(75, 85, 99);
      doc.text(ct(`Elite Patient Care, LLC (Curana Health)  |  ${this.today}`), margin, y);
      y += 8;

      doc.setDrawColor(17, 24, 39);
      doc.setLineWidth(1);
      doc.line(margin, y, pageWidth - margin, y);
      y += 22;
      doc.setTextColor(17, 24, 39);

      // ===== SECTIONS =====
      for (const section of SECTIONS) {
        checkPageBreak(40);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(17, 24, 39);
        doc.text(ct(section.title), margin, y);
        y += 14;

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 41, 59);
        for (const para of section.body) {
          addText(para, 10, 'normal', 13);
          y += 6;
        }
        y += 6;
      }

      // ===== FOOTNOTE =====
      checkPageBreak(40);
      y += 6;
      doc.setDrawColor(209, 213, 219);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 10;
      doc.setTextColor(107, 114, 128);
      addText(FOOTNOTE, 8, 'normal', 10);

      doc.save(`A5069_Contract_Assessment_${new Date().toISOString().slice(0, 10)}.pdf`);
    },
  },
};
</script>

<style scoped>
.memo-h2 {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;
  letter-spacing: 0.01em;
}
.memo p {
  font-size: 0.75rem;
  margin-bottom: 0.45rem;
  line-height: 1.45;
}
.memo strong {
  font-weight: 600;
  color: #111827;
}
</style>
